import { supabase } from './supabaseClient';
import { SelfAssessmentConfig } from './selfAssessmentConfigService';

export interface SelfAssessmentResponse {
  id: string;
  facilityId: string;
  residentId: string;
  metricConfigId: string;
  value: number;
  respondedBy: string;
  respondedByRole: 'STAFF' | 'resident';
  sessionId: string;
  respondedAt: string;
}

export interface SubmitResponseInput {
  metricConfigId: string;
  value: number;
}

/** Most recent value per metric, keyed by metricConfigId */
export type LatestResponseMap = Record<string, SelfAssessmentResponse>;

export class SelfAssessmentResponseService {
  /**
   * Submit a batch of assessment responses as a single session.
   */
  static async submitResponses(
    facilityId: string,
    residentId: string,
    respondedBy: string,
    respondedByRole: 'STAFF' | 'resident',
    responses: SubmitResponseInput[]
  ): Promise<SelfAssessmentResponse[]> {
    const sessionId = crypto.randomUUID();

    const rows = responses.map((r) => ({
      facility_id: facilityId,
      resident_id: residentId,
      metric_config_id: r.metricConfigId,
      value: r.value,
      responded_by: respondedBy,
      responded_by_role: respondedByRole,
      session_id: sessionId
    }));

    const { data, error } = await supabase
      .from('self_assessment_responses')
      .insert(rows)
      .select();

    if (error) {
      console.error('Error submitting self-assessment responses:', error);
      throw error;
    }

    return (data || []).map(this.mapDbToResponse);
  }

  /**
   * Get the latest response for each metric for a given resident.
   * Uses a window function approach: fetch recent responses, then deduplicate client-side.
   */
  static async getLatestResponses(
    facilityId: string,
    residentId: string
  ): Promise<LatestResponseMap> {
    const { data, error } = await supabase
      .from('self_assessment_responses')
      .select('*')
      .eq('facility_id', facilityId)
      .eq('resident_id', residentId)
      .order('responded_at', { ascending: false })
      .limit(200);

    if (error) {
      console.error('Error fetching latest self-assessment responses:', error);
      throw error;
    }

    const byMetric: LatestResponseMap = {};
    for (const row of data || []) {
      const mapped = this.mapDbToResponse(row);
      if (!byMetric[mapped.metricConfigId]) {
        byMetric[mapped.metricConfigId] = mapped;
      }
    }

    return byMetric;
  }

  /**
   * Get the timestamp of the most recent completed assessment session for a resident.
   */
  static async getLastCompletedDate(
    facilityId: string,
    residentId: string
  ): Promise<Date | null> {
    const { data, error } = await supabase
      .from('self_assessment_responses')
      .select('responded_at')
      .eq('facility_id', facilityId)
      .eq('resident_id', residentId)
      .order('responded_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching last assessment date:', error);
      return null;
    }

    if (!data) return null;
    return new Date(data.responded_at);
  }

  /**
   * Check whether a new assessment is due based on the configured frequency.
   */
  static async isDue(
    facilityId: string,
    residentId: string,
    frequency: SelfAssessmentConfig['frequency']
  ): Promise<boolean> {
    const lastDate = await this.getLastCompletedDate(facilityId, residentId);
    if (!lastDate) return true; // Never completed

    const now = new Date();
    const diffMs = now.getTime() - lastDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    const thresholds: Record<string, number> = {
      daily: 1,
      weekly: 7,
      biweekly: 14,
      monthly: 30,
      quarterly: 90,
      biannual: 182,
      annual: 365
    };

    return diffDays >= (thresholds[frequency] ?? 7);
  }

  private static mapDbToResponse(row: any): SelfAssessmentResponse {
    return {
      id: row.id,
      facilityId: row.facility_id,
      residentId: row.resident_id,
      metricConfigId: row.metric_config_id,
      value: Number(row.value),
      respondedBy: row.responded_by,
      respondedByRole: row.responded_by_role,
      sessionId: row.session_id,
      respondedAt: row.responded_at
    };
  }
}
